/**
 * Extension entity class
 */

import { PLATFORM_TYPES } from 'app/core/constants'
import { clone } from 'app/helpers/object'

/**
 * ExtensionFactory
 * returns Extension entity class
 *
 * @class ExtensionFactory
 * @memberof mwApp
 * @see mwApp
 * @return {Function} {@link models.Extension} class
 *
 * @ngdoc factory
 * @name mwApp.factory:ExtensionFactory
 */
/* @ngInject */
export default function ExtensionFactory ($http, $q, alertLiteSvc, Type, Entity, onOffMixin, authSvc) {
  // use generic hasOwnProperty function
  var hasOwnProperty = Object.prototype.hasOwnProperty

  /* === Extension class === */

  /**
   * Extension model
   *
   * @class Extension
   * @classdesc Extension model
   * @memberof models
   * @see models
   * @extends models.Entity
   * @mixes models.onOffMixin
   * @param {Object} [object] - initial object, containing Extension entity fields
   * @return {models.Extension} Extension entity
   */
  function Extension () {
    var obj = Entity.apply(this, arguments)
    if (obj) {
      return obj
    }
  }
  Extension.prototype = Object.create(Entity.prototype)
  Extension.prototype.constructor = Extension

  /* === Extension instance methods & fields === */

  Object.assign(Extension.prototype, {

    /**
     * instance fields
     *
     * @memberof models.Extension#
     * @type {Object}
     */
    _fields: {
      id: Number,
      name: String,
      assigned: Number,
      enabled: Boolean,
      extensionType: String, // EXTENSION_TYPES
      platforms: Type.CSV(';'),
      publicToken: String,
      radioInterval: 0,
      status: Boolean,
      token: Type(String('')),
      tvInterval: 0,
      url: String,
      vodInterval: 0
    },

    /**
     * instance fields by types
     *
     * @memberof models.Extension#
     * @type {Object}
     */
    _fieldsByTypes: {
      common: [
        'id',
        'enabled',
        'extensionType',
        'name',
        'status',
        'token',
        'url'
      ],
      HealthCheck_Node: [
        'publicToken',
        'tvInterval',
        'radioInterval',
        'vodInterval',
        'platforms'
      ]
    },

    /**
     * url template
     *
     * @memberof models.Extension#
     * @type {String}
     */
    _urlTemplate: 'api/extensions/{{::id}}',

    /**
     * public url template
     *
     * @memberof models.Extension#
     * @type {String}
     */
    _publicUrlTemplate: 'extensions/{{::id}}',

    /**
     * Prepare entity, fill fields
     *
     * @memberof models.Extension#
     * @override
     */
    _prepare: function () {
      this.isTvInterval = !!this.tvInterval
      this.isRadioInterval = !!this.radioInterval
      this.isVodInterval = !!this.vodInterval

      if (!this._isNew() && this.extensionType === 'HealthCheck_Node') {
        this.tvIntervalTime = this.tvInterval && this.tvInterval % 60 === 0 ? 'Minutes' : 'Seconds'
        this.radioIntervalTime = this.radioInterval && this.radioInterval % 60 === 0 ? 'Minutes' : 'Seconds'
        this.vodIntervalTime = this.vodInterval && this.vodInterval % 60 === 0 ? 'Minutes' : 'Seconds'

        this.tvInterval = this.tvInterval && this.tvIntervalTime === 'Minutes' ? this.tvInterval / 60 : this.tvInterval
        this.radioInterval = this.radioInterval && this.radioIntervalTime === 'Minutes' ? this.radioInterval / 60 : this.radioInterval
        this.vodInterval = this.vodInterval && this.vodIntervalTime === 'Minutes' ? this.vodInterval / 60 : this.vodInterval
      }
    },

    /**
     * Get underlying object
     *
     * @memberof models.Extension#
     * @return {Object} new underlying object as plain object
     * @override
     */
    _object: function () {
      var ret = Entity.prototype._object.apply(this, arguments)

      // leave only necessary fields for that type of extension node
      var fields = this._fieldsByTypes.common.concat(this._fieldsByTypes[ret.extensionType])
      for (var f in ret) {
        if (hasOwnProperty.call(ret, f) && fields.indexOf(f) === -1) {
          delete ret[f]
        }
      }

      // update some specific node fields
      if (ret.extensionType === 'HealthCheck_Node') {
        ret.tvInterval = this.isTvInterval ? ret.tvInterval : 0
        ret.radioInterval = this.isRadioInterval ? ret.radioInterval : 0
        ret.vodInterval = this.isVodInterval ? ret.vodInterval : 0

        ret.tvInterval = ret.tvInterval && this.tvIntervalTime === 'Minutes' ? ret.tvInterval * 60 : ret.tvInterval
        ret.radioInterval = ret.radioInterval && this.radioIntervalTime === 'Minutes' ? ret.radioInterval * 60 : ret.radioInterval
        ret.vodInterval = ret.vodInterval && this.vodIntervalTime === 'Minutes' ? ret.vodInterval * 60 : ret.vodInterval
      }

      return ret
    },

    /**
     * Get platforms array with checked flags, depending on network platforms array
     *
     * @param {String[]} def - default flags
     * @return {Object[]}
     * @memberof models.Extension#
     */
    _enhancePlatforms: function (def) {
      var ret = clone(PLATFORM_TYPES.filter((p) => {
        return !(!authSvc.hasEnabledFlag('WEB_PLATFORM_ENABLED') && p.name === 'WEB');
      }))
      if (this._isNew() && def) {
        for (let i = ret.length; i--;) {
          if (def.indexOf(ret[i].name) !== -1) {
            ret[i].checked = true
          }
        }
      } else
      if (this.platforms && this.platforms.length) {
        for (let i = ret.length; i--;) {
          ret[i].checked = this.platforms.indexOf(ret[i].name) !== -1
        }
      }
      return ret
    },

    /**
     * Set network platforms array from platforms array with checked flags
     *
     * @param {Object[]} platforms - platforms array
     * @memberof models.Extension#
     */
    _reducePlatforms: function (platforms) {
      this.platforms = []
      for (var i = platforms.length; i--;) {
        if (platforms[i].checked) {
          this.platforms.push(platforms[i].name)
        }
      }
      return this
    }

  }, onOffMixin)

  /* === Extension static methods & fields === */

  // copy all static methods from Entity class
  Object.assign(Extension, Entity)
  Extension._name = 'Extension'
  Extension.init()

  // permissions
  Extension.access = {
    view: 'viewExtension',
    create: 'createExtension',
    edit: 'editExtension',
    delete: 'deleteExtension'
  }

  return Extension
}
